import { getAccessToken } from "../lib/actions"; 
import { useAuthStore } from "@/app/hooks/auth";

/* ---------- helpers mínimos ---------- */
const API_HOST_RAW = (process.env.NEXT_PUBLIC_API_HOST || "").trim();

/** Junta host + path sem gerar '//' nem faltar '/'.
 *  Se o host estiver vazio, usa caminho relativo. */
const buildUrl = (url: string) => {
  const base = API_HOST_RAW.replace(/\/$/, "");           // remove barra final do host
  const path = url.startsWith("/") ? url : `/${url}`;     // garante barra no path
  return base ? `${base}${path}` : path;
};

/** Cria headers válidos p/ fetch; só adiciona Authorization se houver token */
const makeHeaders = (
  base: Record<string, string> | undefined,
  token?: string | null
): Record<string, string> => {
  const h: Record<string, string> = { ...(base || {}) };
  if (token) h.Authorization = `Bearer ${token}`;
  return h;
};

const handleResponse = async (res: Response) => {
  if (res.status === 401) {
    console.warn('⚠️ Token expirado ou inválido. Limpando Zustand e redirecionando...');
    useAuthStore.getState().logout();
    window.location.href = '/';
    throw new Error('Sessão expirada. Redirecionando...');
  }

  const contentType = res.headers.get("content-type");
  const isJson = contentType?.includes("application/json");
  const data = isJson ? await res.json() : await res.text();

  if (!res.ok) {
    const errorMessage = isJson ? (data as any)?.error || (data as any)?.detail || "Erro no servidor" : (data as any);
    throw new Error(errorMessage as string);
  }

  // (este bloco abaixo é redundante; mantive para não mudar semântica)
  if (!res.ok) {
    const error = new Error('Erro no servidor');
    (error as any).response = data;
    throw error;
  }

  return data;
};

/* ---------- serviço ---------- */
const apiService = {
  // get: async (url: string): Promise<any> => {
  //  const token = await getAccessToken();
  //  const headers = makeHeaders(
  //    { Accept: 'application/json', 'Content-Type': 'application/json' },
  //    token
  //  );

  //  const res = await fetch(buildUrl(url), { method: 'GET', headers });
  //  return handleResponse(res);
  // },

  get: async (url: string, options: RequestInit = {}): Promise<any> => {

    const customHeaders = (options.headers as Record<string, string>) || {};
    const explicitToken = customHeaders['Authorization']?.replace('Bearer ', '');
    const token = explicitToken || (await getAccessToken());

    // Injeta os headers padrão + headers customizados passados na chamada
    const headers = makeHeaders(
      { 
        Accept: 'application/json', 
        'Content-Type': 'application/json',
        ...customHeaders 
      },
      token
    );

    const res = await fetch(buildUrl(url), { 
      ...options,
      method: 'GET', 
      headers 
    });

    return handleResponse(res);
  },

  // getPublic: async (url: string): Promise<any> => {
  //  const res = await fetch(buildUrl(url));
  //  return handleResponse(res);
  // },

  getPublic: async (url: string): Promise<any> => {
    const res = await fetch(buildUrl(url), {
      cache: 'no-store', // 🌟 Impede o Next.js de salvar a resposta em cache
      headers: {
        'Content-Type': 'application/json',
      },
    });
    return handleResponse(res);
  },

  getPublicWithoutToken: async (url: string): Promise<any> => {
    const headers = { 
      'Accept': 'application/json', 
      'Content-Type': 'application/json' 
    };

    const res = await fetch(buildUrl(url), { 
      method: 'GET', 
      headers 
    });
    
    return handleResponse(res);
  },

  getWithToken: async (url: string): Promise<any> => {
    const token = await getAccessToken();
    const headers = makeHeaders({}, token);

    const res = await fetch(buildUrl(url), { method: 'GET', headers });
    return handleResponse(res);
  },

  deleteWithToken: async (url: string): Promise<any> => {
    const token = await getAccessToken();
    const headers = makeHeaders(
      { Accept: 'application/json', 'Content-Type': 'application/json' },
      token
    );

    const res = await fetch(buildUrl(url), { method: 'DELETE', headers });
    return handleResponse(res);
  },

  post: async (url: string, data: any): Promise<any> => {
    const token = await getAccessToken();
    const headers = makeHeaders({}, token);

    const res = await fetch(buildUrl(url), {
      method: 'POST',
      body: data, // pode ser FormData ou string
      headers,
    });

    return handleResponse(res);
  },

  postWithToken: async (url: string, data: any): Promise<any> => {
    const token = await getAccessToken();
    const isFormData = data instanceof FormData;

    const headers = makeHeaders(
      isFormData ? {} : { 'Content-Type': 'application/json' },
      token
    );

    const res = await fetch(buildUrl(url), {
      method: 'POST',
      body: isFormData ? data : JSON.stringify(data),
      headers,
    });

    return handleResponse(res);
  },

  postWithTokenFormData: async function (
    url: string,
    data: any,
    isFormData: boolean = false
  ): Promise<any> {
    const token = await getAccessToken();

    const headers = makeHeaders(
      isFormData ? {} : { 'Content-Type': 'application/json' },
      token
    );

    const response = await fetch(buildUrl(url), {
      method: 'POST',
      headers,
      body: isFormData ? data : JSON.stringify(data),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error((errorData as any)?.error || 'Erro ao enviar dados');
    }

    return response.json();
  },

  postWithoutToken: async (url: string, data: any): Promise<any> => {
    const headers: Record<string, string> = {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    };

    const res = await fetch(buildUrl(url), {
      method: 'POST',
      body: data, // se for JSON já stringificado, ok
      headers,
    });

    return handleResponse(res);
  },

  postWithoutTokenPlus: async (url: string, data: any): Promise<any> => { 
    const headers: Record<string, string> = {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    };

    const res = await fetch(buildUrl(url), {
      method: 'POST',
      body: JSON.stringify(data),
      headers,
    });

    return handleResponse(res);
  },
};

export default apiService;
